import { Button, Drawer } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../ConfirmDialog';

export interface VersionEntry {
  id: string;
  label: string;
  author?: string;
  createdAt: string;
  snapshot: string;
  current?: boolean;
}

interface VersionDrawerProps {
  open: boolean;
  onClose: () => void;
  versions: VersionEntry[];
  currentContent?: string;
  onRestore: (version: VersionEntry) => Promise<void> | void;
}

export function VersionDrawer({ open, onClose, versions, currentContent = '', onRestore }: VersionDrawerProps) {
  const { t } = useTranslation('components');
  const [activeId, setActiveId] = useState<string | null>(versions[0]?.id ?? null);
  const confirm = useConfirm();
  const active = versions.find((v) => v.id === activeId);

  const diffSummary = (a: string, b: string) => {
    const aw = a.split(/\s+/).length;
    const bw = b.split(/\s+/).length;
    return t('versionDrawer.diffSummary', { a: aw, b: bw });
  };

  const handleRestore = async () => {
    if (!active) return;
    const ok = await confirm({
      title: t('versionDrawer.restoreConfirmTitle'),
      description: t('versionDrawer.restoreConfirmDescription'),
    });
    if (ok) await onRestore(active);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('versionDrawer.title')}
      width={720}
      destroyOnHidden
      extra={
        active && !active.current ? (
          <Button type="primary" onClick={() => void handleRestore()}>{t('versionDrawer.restore')}</Button>
        ) : null
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }}>
        <div className="ds-versions">
          {versions.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)' }}>{t('versionDrawer.empty')}</div>
          ) : (
            versions.map((v) => (
              <button
                key={v.id}
                type="button"
                className="ds-version-item"
                data-current={v.id === activeId ? 'true' : 'false'}
                onClick={() => setActiveId(v.id)}
              >
                <span>
                  <div className="ds-version-item__title">{v.label}{v.current ? ` • ${t('versionDrawer.current')}` : ''}</div>
                  <div className="ds-version-item__meta">{v.author ? `${v.author} · ` : ''}{v.createdAt}</div>
                </span>
              </button>
            ))
          )}
        </div>

        {active ? (
          <div>
            <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)', marginBottom: 6 }}>
              {diffSummary(active.snapshot, currentContent)}
            </div>
            <div className="ds-diff">
              <div>
                <div className="ds-diff__panel-head">{t('versionDrawer.selectedVersion')}</div>
                <div className="ds-diff__panel">{active.snapshot.replace(/<[^>]+>/g, '') || t('versionDrawer.emptyContent')}</div>
              </div>
              <div>
                <div className="ds-diff__panel-head">{t('versionDrawer.currentContent')}</div>
                <div className="ds-diff__panel">{currentContent.replace(/<[^>]+>/g, '') || t('versionDrawer.emptyContent')}</div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--ds-text-tertiary)' }}>{t('versionDrawer.selectVersion')}</div>
        )}
      </div>
    </Drawer>
  );
}
